import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { clientsApi, type ClientDto } from "@/lib/clients-accounts";

export const newClientSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  emailAddress: z.string().email().or(z.literal("")).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  mobilePhone: z.string().max(40).nullable().optional(),
  companyId: z.number().int().positive().nullable().optional(),
  companyName: z.string().max(200).nullable().optional(),
  departmentName: z.string().max(100).nullable().optional(),
  branch: z.string().max(50).nullable().optional(),
});
export type NewClientForm = z.infer<typeof newClientSchema>;

export interface NewClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (client: ClientDto) => void;
  /** When set, preselects this company for the new client (e.g. from a company's contacts list). */
  defaultCompanyId?: number;
}

export function NewClientDialog({ open, onOpenChange, onCreated, defaultCompanyId }: NewClientDialogProps) {
  const qc = useQueryClient();

  const createMut = useMutation({
    mutationFn: (values: NewClientForm) =>
      clientsApi.create({
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.emailAddress ?? null,
        phone: values.phone ?? null,
        mobilePhone: values.mobilePhone ?? null,
        companyId: values.companyId ?? defaultCompanyId ?? null,
        companyName: values.companyName ?? null,
        departmentName: values.departmentName ?? null,
        branch: values.branch ?? null,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      onCreated(created);
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
        </DialogHeader>
        <EntityForm
          schema={newClientSchema}
          defaultValues={{
            firstName: "",
            lastName: "",
            emailAddress: "",
            phone: "",
            mobilePhone: "",
            companyId: defaultCompanyId ?? null,
            companyName: "",
            departmentName: "",
            branch: "",
          }}
          onSubmit={(v) => createMut.mutate(v)}
        >
          {(f) => (
            <>
              <div className="max-h-[60vh] space-y-5 overflow-y-auto pl-1 -ml-1 pr-4 py-2 -mr-2">
              <div className="space-y-1.5">
                <Label htmlFor="c-fn" className="text-slate-600">First name *</Label>
                <Input id="c-fn" {...f.register("firstName")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-ln" className="text-slate-600">Last name *</Label>
                <Input id="c-ln" {...f.register("lastName")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-em" className="text-slate-600">Email</Label>
                <Input id="c-em" type="email" {...f.register("emailAddress")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-ph" className="text-slate-600">Phone</Label>
                <Input id="c-ph" {...f.register("phone")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-mp" className="text-slate-600">Mobile</Label>
                <Input id="c-mp" {...f.register("mobilePhone")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-co" className="text-slate-600">Company name</Label>
                <Input id="c-co" {...f.register("companyName")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-dn" className="text-slate-600">Department</Label>
                <Input id="c-dn" {...f.register("departmentName")} className="bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-br" className="text-slate-600">Branch</Label>
                <Input id="c-br" {...f.register("branch")} className="bg-slate-100" />
              </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full px-6"
                  onClick={() => onOpenChange(false)}
                  disabled={createMut.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full px-6 bg-[#1a2f4c] hover:bg-[#111e33] text-white" disabled={createMut.isPending}>
                  {createMut.isPending ? "Creating…" : "Create client"}
                </Button>
              </DialogFooter>
            </>
        )}
        </EntityForm>
      </DialogContent>
    </Dialog>
  );
}
